import { useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import useMerchantPortalAxios, {
  useMerchantPortalFormData,
} from "../../utils/axios";
import {
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";
import useResponse from "../../app/useResponse";

export default function useNewBslAccountAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const formClient = useMerchantPortalFormData();
  const { successResponse, errorResponse } = useResponse();

  const [bslTransactionLoading, setBslTransactionLoading] = useState(false);
  const [s2sData, setS2sData] = useState([]);
  const [s2sLoading, setS2sLoading] = useState(true);
  const [totalNbOfPage, setTotalNbOfPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);

  async function ApplyForIban() {
    return GetIbanBranches();
  }

  async function GetIbanBranches() {
    try {
      const res = await client.post("/v1/BankAccount/ApplyForIban", {
        SessionID: sessionStorage.getItem("SessionID"),
      });

      const payload = res?.data?.Payload;
      dispatch(
        setFeatureData({
          section: "details",
          key: "applyForIbanFlagCode",
          value: payload?.flagCode,
        }),
      );

      if (payload?.flagCode === 1) {
        successResponse(payload);
      } else if (payload?.flagCode === 2) {
        dispatch(
          setFeatureData({
            section: "details",
            key: "ibanBranches",
            value: payload?.Branches,
          }),
        );
        sessionStorage.setItem("verificationRef", payload?.VerificationRef)
      }

      return payload;
    } catch (error) {
      errorResponse(error);
      throw error;
    }
  }

  async function GetBankAccountOverview() {
    const res = await client.post("/v1/BankAccount/GetBankAccountOverview", {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    const payload = res?.data?.Payload;
    const accounts =
      payload?.BankAccountCurrenciesOverview ?? payload?.CurrenciesOverview ?? [];

    dispatch(
      setTableData({
        key: "bslAccountOverview",
        data: accounts,
      }),
    );

    return accounts;
  }

    async function GetBankAccountStatus() {
    const res = await client.post("/v1/BankAccount/GetBankAccountStatus", {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    dispatch(
      setFeatureData({
        section: "summary",
        key: "bankAccountStatus",
        value: res?.data?.Payload?.bankAccountStatus,
      }),
    );

    return res?.data?.Payload?.bankAccountStatus;
  }

  async function GetBankAccountBalance(dateFrom, dateTo) {
    const response = await client.post("/v1/Overview/GetBankAccountBalance", {
      SessionID: sessionStorage.getItem("SessionID"),
      DateFrom: dateFrom,
      DateTo: dateTo,
    });

    const currenciesOverview = response?.data?.Payload?.CurrenciesOverview || [];

    dispatch(
      setFeatureData({
        section: "summary",
        key: "bslCurrenciesOverview",
        value: currenciesOverview,
      }),
    );

    return response?.data;
  }

  async function GetOutwardTransfers(pageNb, dateFrom, dateTo, type) {
    setS2sLoading(true);

    try {
      const res = await client.post("/v1/BankAccount/GetOutwardTransfers", {
        SessionID: sessionStorage.getItem("SessionID"),
        page: pageNb,
        pageSize: 10,
        DateFrom: dateFrom,
        DateTo: dateTo,
        Type: type,
      });

      const transfers = res?.data?.Payload?.CorporateOutwardTransfers || [];
      setS2sData(transfers);

      if (transfers.length === 0) {
        setTotalNbOfPage(0);
        setTotalCount(0);
      } else {
        setTotalNbOfPage(transfers[0]?.TotalPages || 0);
        setTotalCount(transfers[0]?.TotalCount || 0);
      }

      if (res?.data?.Payload?.ProofOfPaymentUrl) {
        sessionStorage.setItem(
          "S2sTransferTemplate",
          res.data.Payload.ProofOfPaymentUrl,
        );
      }

      return transfers;
    } finally {
      setS2sLoading(false);
    }
  }

  async function PushOutwardTransfer(payload) {
    const form = new FormData();
    form.append("CurrencyID", String(payload?.CurrencyID ?? ""));
    form.append("Amount", String(payload?.Amount ?? ""));
    form.append(
      "BeneficiaryInfo.Name",
      payload?.BeneficiaryInfo?.BeneficiaryName ?? "",
    );
    form.append(
      "BeneficiaryInfo.Country",
      payload?.BeneficiaryInfo?.Country ?? "",
    );
    form.append("BeneficiaryInfo.City", payload?.BeneficiaryInfo?.City ?? "");
    form.append(
      "BeneficiaryInfo.Street",
      payload?.BeneficiaryInfo?.Street ?? "",
    );
    form.append(
      "BeneficiaryInfo.Building",
      payload?.BeneficiaryInfo?.Building ?? "",
    );
    form.append("BankInfo.IBANNumber", payload?.BankInfo?.IbanNumber ?? "");
    form.append("BankInfo.Name", payload?.BankInfo?.BankName ?? "");
    form.append("BankInfo.Country", payload?.BankInfo?.BankCity ?? "");
    form.append("BankInfo.Swift", payload?.BankInfo?.Swift ?? "");
    form.append(
      "BankInfo.ReasonForTransfer",
      payload?.BankInfo?.ReasonForTransfer ?? "",
    );
    form.append("AuthorizationCode", payload?.AuthorizationCode ?? "");
    form.append(
      "SessionID",
      payload?.SessionID ?? sessionStorage.getItem("SessionID") ?? "",
    );

    const proof = payload?.BankInfo?.ProofOfPayment;
    if (proof instanceof File || proof instanceof Blob) {
      form.append("BankInfo.ProofOfPayment", proof, proof.name || "proof");
    } else {
      form.append("BankInfo.ProofOfPayment", "");
    }

    const res = await formClient.post("/v1/BankAccount/PushOutwardTransfer", form);
    return res?.data?.Payload;
  }

  async function PushS2BTransfer(payload) {
    const form = new FormData();

    const normalizeDirection = (dir) => {
      if (dir == null) return "";
      if (typeof dir === "number") return String(dir);
      const normalized = String(dir).trim().toUpperCase();
      if (normalized === "IN") return "1";
      if (normalized === "OUT") return "2";
      return normalized;
    };

    form.append("CurrencyID", String(payload?.CurrencyID ?? ""));
    form.append("Amount", String(payload?.Amount ?? ""));
    form.append("TransferDirection", normalizeDirection(payload?.TransferDirection));
    form.append("AuthorizationCode", payload?.AuthorizationCode ?? "");
    form.append(
      "SessionID",
      payload?.SessionID ?? sessionStorage.getItem("SessionID") ?? "",
    );

    if (payload?.ProofOfPayment instanceof File) {
      form.append(
        "ProofOfPayment",
        payload.ProofOfPayment,
        payload.ProofOfPayment.name,
      );
    } else {
      form.append("ProofOfPayment", "");
    }

    const res = await client.post("/v1/BankAccount/PushCorporateP2PTransfer", form, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res?.data?.Payload;
  }

  async function GetBslTransactionHistory(
    pageNb,
    dateFrom,
    dateTo,
    type,
    currencyId,
  ) {
    setBslTransactionLoading(true);
    dispatch(setTableLoading({ key: "transactionHistoryBsl", loading: true }));

    try {
      const res = await client.post("/v1/BankAccount/GetBslTransactionHistory", {
        SessionID: sessionStorage.getItem("SessionID"),
        page: pageNb,
        PageSize: 10,
        DateFrom: dateFrom,
        DateTo: dateTo,
        Type: type,
        CurrencyId: currencyId,
      });

      const transactions = res?.data?.Payload?.BslTransactions || [];
      dispatch(
        setTableData({
          key: "transactionHistoryBsl",
          data: transactions,
          pagination: {
            totalPages: transactions.length ? transactions[0]?.TotalPages || 0 : 0,
            totalCount: transactions.length ? transactions[0]?.TotalCount || 0 : 0,
          },
        }),
      );

      if (transactions.length === 0) {
        setTotalNbOfPage(0);
        setTotalCount(0);
      } else {
        setTotalNbOfPage(transactions[0]?.TotalPages || 0);
        setTotalCount(transactions[0]?.TotalCount || 0);
      }

      return transactions;
    } finally {
      setBslTransactionLoading(false);
      dispatch(setTableLoading({ key: "transactionHistoryBsl", loading: false }));
    }
  }

  async function GetCorporateP2PTransfers(
    pageNb,
    dateFrom,
    dateTo,
    type,
    transferDirection,
  ) {
    setS2sLoading(true);

    try {
      const res = await client.post("v1/BankAccount/GetCorporateP2PTransfers", {
        SessionID: sessionStorage.getItem("SessionID"),
        page: pageNb,
        PageSize: 10,
        DateFrom: dateFrom,
        DateTo: dateTo,
        type,
        transferDirection,
      });

      const transfers = res?.data?.Payload?.CorporateP2PTransfers || [];
      setS2sData(transfers);

      if (transfers.length === 0) {
        setTotalNbOfPage(0);
        setTotalCount(0);
      } else {
        setTotalNbOfPage(transfers[0]?.TotalPages || 0);
        setTotalCount(transfers[0]?.TotalCount || 0);
      }

      if (res?.data?.Payload?.TemplateURL) {
        sessionStorage.setItem("S2sTransferTemplate", res.data.Payload.TemplateURL);
      }

      return transfers;
    } finally {
      setS2sLoading(false);
    }
  }

  async function SaveModificationRequestForm(payload) {
    const response = await client.post(
      "/v1/BankAccount/SaveModificationRequestForm",
      payload,
    );
    return response?.data?.Payload;
  }

  async function SubmitApplyForIban(payload) {
    try {
      const res = await client.post("/v1/BankAccount/SubmitApplyForIban", payload);
      successResponse(res?.data?.Payload);
      return res?.data?.Payload;
    } catch (error) {
      errorResponse(error);
      throw error;
    }
  }

  async function SubmitIbanApplication({ branchID, verificationRef }) {
    return SubmitApplyForIban({
      SessionID: sessionStorage.getItem("SessionID"),
      VerificationRef: verificationRef,
      BranchID: Number(branchID),
    });
  }

  async function AccessToken() {
    const res = await axios.post("https://suyool.com/api/create-token", {
      identifier: "trsbslb",
    });

    if (res?.data?.token) {
      sessionStorage.setItem("BSLAccessToken", res.data.token);
    }

    return res?.data?.token;
  }

  async function DownloadTransferStatment(providerID, transferIdentifier) {
    const token = sessionStorage.getItem("BSLAccessToken");

    const res = await client.post(
      "https://gateway1.api.suyool.com/Transfers/Transfer/DownloadTransferStatement",
      { providerID, transferIdentifier },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return res?.data;
  }

  async function GetIBANCertificate() {
    const res = await client.post("/v1/BankAccount/GetIBANCertificate", {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    return res?.data?.Payload;
  }

  return {
    ApplyForIban,
    GetIbanBranches,
    GetBankAccountOverview,
    GetBankAccountStatus,
    GetBankAccountBalance,
    GetOutwardTransfers,
    PushOutwardTransfer,
    PushS2BTransfer,
    GetBslTransactionHistory,
    GetCorporateP2PTransfers,
    SaveModificationRequestForm,
    SubmitApplyForIban,
    SubmitIbanApplication,
    AccessToken,
    DownloadTransferStatment,
    GetIBANCertificate,
    s2sData,
    s2sLoading,
    totalNbOfPage,
    totalCount,
    bslTransactionLoading,
  };
}
